import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import Logo from './Logo';
import { OrderIdBadge } from './shared';
import { apiUrl } from '@/config/api';
import { 
  Users, 
  Crown, 
  UserPlus, 
  Mail, 
  TrendingUp, 
  CheckCircle, 
  XCircle, 
  Clock,
  BarChart3,
  Shield,
  Trash2,
  Eye,
  Send,
  AlertTriangle,
  KeyRound
} from 'lucide-react';

interface User {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  created_at: string;
  phone_number?: string;
  profile_completion_percentage?: number;
  tier?: 'free' | 'basic' | 'premium';
}

interface TailorProfile {
  id: number;
  user_id: number;
  display_name: string;
  tagline: string;
  slug: string;
  verified_status: 'unverified' | 'pending' | 'verified';
  pickup_radius_km: number;
  template_style: string;
  created_at: string;
  updated_at: string;
}

interface AdminStats {
  total_users: number;
  total_customers: number;
  total_tailors: number;
  total_orders: number;
  active_users: number;
  pending_orders: number;
  completed_orders: number;
  revenue: number;
  verified_tailors: number;
  pending_verifications: number;
}

interface InvitationData {
  email: string;
  role: 'tailor' | 'customer';
  message?: string;
}

const AdminDashboard: React.FC = () => {
  const { user, token, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'tailors' | 'customers' | 'all-users' | 'invitations' | 'analytics'>('overview');
  const [users, setUsers] = useState<User[]>([]);
  const [tailorProfiles, setTailorProfiles] = useState<TailorProfile[]>([]);
  const [stats, setStats] = useState<AdminStats>({
    total_users: 0,
    total_customers: 0,
    total_tailors: 0,
    total_orders: 0,
    active_users: 0,
    pending_orders: 0,
    completed_orders: 0,
    revenue: 0,
    verified_tailors: 0,
    pending_verifications: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showTierModal, setShowTierModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState<User | null>(null);
  const [invitationData, setInvitationData] = useState<InvitationData>({
    email: '',
    role: 'tailor',
    message: ''
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      logout();
      return;
    }
    if (token) {
      fetchDashboardData();
    }
  }, [user, token, logout]);

  const fetchDashboardData = async () => {
    try {
      console.log('🔄 DEBUG: Starting fetchDashboardData...');
      console.log('🔑 DEBUG: Using token from useAuth:', token ? 'Present' : 'Missing');
      
      if (!token) {
        console.error('❌ DEBUG: No token available for admin requests');
        return;
      }
      
      // Fetch users
      console.log('📡 DEBUG: Fetching users from /api/admin/users...');
      const usersResponse = await fetch(apiUrl('/api/admin/users'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        console.log('✅ DEBUG: Users data received:', usersData.users?.length, 'users');
        console.log('🔍 DEBUG: User tiers:', usersData.users?.map(u => ({ id: u.id, email: u.email, tier: u.tier })));
        setUsers(usersData.users || []);
      } else {
        console.error('❌ DEBUG: Failed to fetch users:', usersResponse.status);
      }
      
      // Fetch tailor profiles
      const profilesResponse = await fetch(apiUrl('/api/admin/tailor-profiles'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (profilesResponse.ok) {
        const profilesData = await profilesResponse.json();
        setTailorProfiles(profilesData.profiles || []);
      }
      
      // Fetch stats
      const statsResponse = await fetch(apiUrl('/api/admin/stats'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
    } catch (_error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleViewUser = async (userId: number) => {
    try {
      if (!token) {
        console.error('❌ DEBUG: No token available for admin requests');
        return;
      }
      
      const url = apiUrl(`/api/admin/users/${userId}`);
      console.log('🔍 DEBUG: Making request to:', url);
      console.log('🔍 DEBUG: User ID:', userId);
      console.log('🔍 DEBUG: Token available:', !!token);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('🔍 DEBUG: Response status:', response.status);
      console.log('🔍 DEBUG: Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (response.ok) {
        const responseData = await response.json();
        console.log('🔍 DEBUG: User details response:', responseData);
        
        // Extract user data from response
        const userData = responseData.user || responseData;
        setSelectedUser(userData);
        setShowUserModal(true);
      } else {
        const errorText = await response.text();
        console.error('❌ DEBUG: Failed to fetch user details:', response.status, response.statusText);
        console.error('❌ DEBUG: Error response:', errorText);
        alert('Failed to fetch user details');
      }
    } catch (_error) {
      console.error('❌ DEBUG: Error fetching user details:', error);
      alert('Error fetching user details');
    }
  };

  const handleUpgradeTier = async (userId: string, newTier: 'free' | 'basic' | 'premium') => {
    if (!userId) {
      alert('No user selected for tier upgrade');
      return;
    }
    
    if (!confirm(`Are you sure you want to upgrade this user to ${newTier} tier?`)) return;
    
    setActionLoading(parseInt(userId));
    try {
      if (!token) {
        console.error('❌ DEBUG: No token available for admin requests');
        return;
      }
      
      console.log('🔑 DEBUG: Using token from useAuth:', token ? 'Present' : 'Missing');
      
      const response = await fetch(apiUrl(`/api/admin/users/${userId}/tier`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tier: newTier })
      });
      
      console.log('📡 DEBUG: API response status:', response.status);
      console.log('📡 DEBUG: API response headers:', Object.fromEntries(response.headers.entries()));
      
      let responseData;
      try {
        responseData = await response.json();
        console.log('📡 DEBUG: API response body:', responseData);
      } catch (parseError) {
        const responseText = await response.text();
        console.log('📡 DEBUG: API response body (text):', responseText);
        responseData = { error: responseText };
      }
      
      if (response.ok) {
        console.log(`✅ DEBUG: Successfully upgraded user ${userId} to ${newTier} tier`);
        alert(`User upgraded to ${newTier} tier successfully`);
        console.log('🔄 DEBUG: Refreshing dashboard data...');
        setShowTierModal(false); // Close the modal
        fetchDashboardData(); // Refresh data
      } else {
        let errorMessage = 'Unknown error';
        if (responseData.error) {
          errorMessage = responseData.error.detail || responseData.error.message || responseData.error.error || `HTTP ${response.status}`;
        } else if (responseData.detail) {
          errorMessage = responseData.detail;
        } else if (responseData.message) {
          errorMessage = responseData.message;
        } else {
          errorMessage = `HTTP ${response.status}`;
        }
        alert(`Failed to upgrade user: ${errorMessage}`);
      }
    } catch (_error) {
      console.error('Error upgrading user:', error);
      alert('Error upgrading user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleVerifyTailor = async (profileId: number) => {
    if (!confirm('Are you sure you want to verify this tailor?')) return;
    
    setActionLoading(profileId);
    try {
      if (!token) {
        console.error('❌ DEBUG: No token available for admin requests');
        return;
      }
      
      const response = await fetch(apiUrl(`/api/admin/tailor-profiles/${profileId}/verify`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        alert('Tailor verified successfully');
        fetchDashboardData(); // Refresh data
      } else {
        const errorData = await response.json();
        alert(`Failed to verify tailor: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (_error) {
      console.error('Error verifying tailor:', error);
      alert('Error verifying tailor');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendInvitation = async () => {
    if (!invitationData.email.trim()) {
      alert('Please enter an email address');
      return;
    }

    setActionLoading(-1);
    try {
      if (!token) {
        console.error('❌ DEBUG: No token available for admin requests');
        return;
      }
      
      const response = await fetch(apiUrl('/api/admin/send-invitation'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(invitationData)
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(`Invitation sent successfully! Invitation link: ${result.invitation_link}`);
        setShowInviteModal(false);
        setInvitationData({ email: '', role: 'tailor', message: '' });
      } else {
        const errorData = await response.json();
        alert(`Failed to send invitation: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (_error) {
      console.error('Error sending invitation:', error);
      alert('Error sending invitation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) {
      alert('No user selected for deletion');
      return;
    }
    
    if (!confirm(`Are you sure you want to permanently delete user "${userToDelete.full_name}" (${userToDelete.email})? This action cannot be undone.`)) {
      return;
    }
    
    setActionLoading(parseInt(userToDelete.id));
    try {
      if (!token) {
        console.error('❌ DEBUG: No token available for admin requests');
        return;
      }
      
      console.log('🗑️ Deleting user:', userToDelete.email);
      
      const response = await fetch(apiUrl(`/api/admin/users/${userToDelete.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('🗑️ Delete response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ User deleted successfully:', result.message);
        alert(`User "${userToDelete.full_name}" has been deleted successfully`);
        setShowDeleteModal(false);
        setUserToDelete(null);
        fetchDashboardData(); // Refresh data
      } else {
        const errorData = await response.json();
        console.error('❌ Failed to delete user:', errorData);
        alert(`Failed to delete user: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (_error) {
      console.error('Error deleting user:', error);
      alert('Error deleting user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async () => {
    if (!userToResetPassword) {
      alert('No user selected for password reset');
      return;
    }
    
    if (!confirm(`Are you sure you want to reset the password for "${userToResetPassword.full_name}" (${userToResetPassword.email})? They will need to change it on their next login.`)) {
      return;
    }
    
    setActionLoading(parseInt(userToResetPassword.id));
    try {
      if (!token) {
        console.error('❌ DEBUG: No token available for admin requests');
        return;
      }
      
      console.log('🔑 Resetting password for user:', userToResetPassword.email);
      
      const response = await fetch(apiUrl(`/api/admin/users/${userToResetPassword.id}/reset-password`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('🔑 Reset password response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Password reset successfully:', result.message);
        alert(`${result.message}\n\nThey will be asked to choose a new password on their next sign-in.`);
        setShowPasswordResetModal(false);
        setUserToResetPassword(null);
        fetchDashboardData(); // Refresh data
      } else {
        const errorData = await response.json();
        console.error('❌ Failed to reset password:', errorData);
        alert(`Failed to reset password: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (_error) {
      console.error('Error resetting password:', error);
      alert('Error resetting password');
    } finally {
      setActionLoading(null);
    }
  };

  const copyInvitationLink = (link: string) => {
    navigator.clipboard.writeText(link);
    alert('Invitation link copied to clipboard!');
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'premium': return 'bg-purple-100 text-purple-800';
      case 'basic': return 'bg-blue-100 text-blue-800';
      case 'free': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getVerificationColor = (status: string) => {
    switch (status) {
      case 'verified': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'unverified': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCompletionColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Enhanced Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600">
              <Users className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.total_users}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600">
              <Users className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Customers</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.total_customers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 text-purple-600">
              <Crown className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Tailors</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.total_tailors}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100 text-yellow-600">
              <Shield className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Verified</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.verified_tailors}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-100 text-orange-600">
              <Clock className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.pending_verifications}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => setShowInviteModal(true)}
            className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
          >
            <UserPlus className="w-6 h-6 text-gray-400 mr-2" />
            <span className="text-sm font-medium text-gray-600">Invite New Tailor</span>
          </button>
          <button
            onClick={() => setActiveTab('tailors')}
            className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors"
          >
            <Crown className="w-6 h-6 text-gray-400 mr-2" />
            <span className="text-sm font-medium text-gray-600">Manage Tailors</span>
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors"
          >
            <BarChart3 className="w-6 h-6 text-gray-400 mr-2" />
            <span className="text-sm font-medium text-gray-600">View Analytics</span>
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="text-sm text-gray-600">New tailor registration: premium.tailor@masterji.com</span>
              <span className="text-xs text-gray-400">2 minutes ago</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
              <span className="text-sm text-gray-600">New order placed: <OrderIdBadge orderNumber="12345" compact /></span>
              <span className="text-xs text-gray-400">15 minutes ago</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
              <span className="text-sm text-gray-600">Tailor account verified: master@tailor.com</span>
              <span className="text-xs text-gray-400">1 hour ago</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTailors = () => (
    <div className="space-y-6">
      {/* Tailor Management Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Tailor Management</h3>
          <button
            onClick={() => setShowInviteModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Invite New Tailor
          </button>
        </div>
        
        {/* Tailor Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{stats.total_tailors}</div>
            <div className="text-sm text-gray-600">Total Tailors</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{stats.verified_tailors}</div>
            <div className="text-sm text-gray-600">Verified</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">{stats.pending_verifications}</div>
            <div className="text-sm text-gray-600">Pending</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">
              {Math.round((stats.verified_tailors / Math.max(stats.total_tailors, 1)) * 100)}%
            </div>
            <div className="text-sm text-gray-600">Verification Rate</div>
          </div>
        </div>
      </div>

      {/* Tailors Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">All Tailors</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verification</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profile</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.filter(u => u.role === 'tailor').map((user) => {
                const profile = tailorProfiles.find(p => p.user_id === parseInt(user.id));
                return (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.full_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTierColor(user.tier || 'free')}`}>
                        {user.tier || 'free'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {profile ? (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getVerificationColor(profile.verified_status)}`}>
                          {profile.verified_status}
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          No Profile
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div 
                            className={`h-2 rounded-full ${getCompletionColor(user.profile_completion_percentage || 0)}`}
                            style={{ width: `${user.profile_completion_percentage || 0}%` }}
                          ></div>
                        </div>
                        <span className={`text-xs font-medium ${getCompletionColor(user.profile_completion_percentage || 0)}`}>
                          {user.profile_completion_percentage || 0}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleViewUser(parseInt(user.id))}
                          className="text-blue-600 hover:text-blue-900"
                          disabled={actionLoading === parseInt(user.id)}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => {
                            setSelectedUser(user);
                            setShowTierModal(true);
                          }}
                          className="text-purple-600 hover:text-purple-900"
                          disabled={actionLoading === parseInt(user.id)}
                          title="Upgrade Tier"
                        >
                          <Crown className="w-4 h-4" />
                        </button>
                        {profile ? (
                          <button 
                            onClick={() => handleVerifyTailor(profile.id)}
                            className="text-green-600 hover:text-green-900"
                            disabled={actionLoading === profile.id}
                            title="Verify Tailor"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        ) : (
                          <button 
                            onClick={() => alert('This tailor needs to complete their profile setup first')}
                            className="text-gray-400 cursor-not-allowed"
                            title="Profile Required - Tailor must complete profile setup"
                            disabled
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button 
                          onClick={() => {
                            setUserToResetPassword(user);
                            setShowPasswordResetModal(true);
                          }}
                          className="text-orange-600 hover:text-orange-900"
                          disabled={actionLoading === parseInt(user.id)}
                          title="Reset Password"
                        >
                          <KeyRound className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => {
                            setUserToDelete(user);
                            setShowDeleteModal(true);
                          }}
                          className="text-red-600 hover:text-red-900"
                          disabled={actionLoading === parseInt(user.id)}
                          title="Delete User"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderCustomers = () => (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Customer Users</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profile</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.filter(u => u.role === 'customer').map((user) => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.full_name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTierColor(user.tier || 'free')}`}>
                    {user.tier || 'free'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                      <div 
                        className={`h-2 rounded-full ${getCompletionColor(user.profile_completion_percentage || 0)}`}
                        style={{ width: `${user.profile_completion_percentage || 0}%` }}
                      ></div>
                    </div>
                    <span className={`text-xs font-medium ${getCompletionColor(user.profile_completion_percentage || 0)}`}>
                      {user.profile_completion_percentage || 0}%
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => handleViewUser(parseInt(user.id))}
                      className="text-indigo-600 hover:text-indigo-900"
                      disabled={actionLoading === parseInt(user.id)}
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => {
                        setSelectedUser(user);
                        setShowTierModal(true);
                      }}
                      className="text-purple-600 hover:text-purple-900"
                      disabled={actionLoading === parseInt(user.id)}
                      title="Upgrade Tier"
                    >
                      <Crown className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => {
                        setUserToDelete(user);
                        setShowDeleteModal(true);
                      }}
                      className="text-red-600 hover:text-red-900"
                      disabled={actionLoading === parseInt(user.id)}
                      title="Delete User"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderAllUsers = () => (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">All Users</h3>
          <div className="text-sm text-gray-500">
            Showing {users.length} users (all roles)
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.full_name || 'N/A'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user.role === 'admin' ? 'bg-red-100 text-red-800' :
                    user.role === 'tailor' ? 'bg-purple-100 text-purple-800' :
                    user.role === 'customer' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {user.role || 'unknown'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTierColor(user.tier || 'free')}`}>
                    {user.tier || 'free'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => handleViewUser(parseInt(user.id))}
                      className="text-indigo-600 hover:text-indigo-900"
                      disabled={actionLoading === parseInt(user.id)}
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {user.role !== 'admin' && (
                      <>
                        <button 
                          onClick={() => {
                            setSelectedUser(user);
                            setShowTierModal(true);
                          }}
                          className="text-purple-600 hover:text-purple-900"
                          disabled={actionLoading === parseInt(user.id)}
                          title="Upgrade Tier"
                        >
                          <Crown className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => {
                            setUserToDelete(user);
                            setShowDeleteModal(true);
                          }}
                          className="text-red-600 hover:text-red-900"
                          disabled={actionLoading === parseInt(user.id)}
                          title="Delete User"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderInvitations = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Send Invitations</h3>
          <button
            onClick={() => setShowInviteModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            <Send className="w-4 h-4 mr-2" />
            Send New Invitation
          </button>
        </div>
        
        <div className="text-center py-8">
          <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">Invitation Management</h4>
          <p className="text-gray-600 mb-4">Send personalized invitations to tailors and customers</p>
          <button
            onClick={() => setShowInviteModal(true)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Send First Invitation
          </button>
        </div>
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-6">Analytics & Reports</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-4">User Growth</h4>
            <div className="h-64 bg-gray-100 rounded flex items-center justify-center">
              <p className="text-gray-500">Chart will be implemented here</p>
            </div>
          </div>
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-4">Tailor Verification Status</h4>
            <div className="h-64 bg-gray-100 rounded flex items-center justify-center">
              <p className="text-gray-500">Chart will be implemented here</p>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-6 bg-gray-50 rounded-lg">
            <div className="text-3xl font-bold text-blue-600">{stats.total_users}</div>
            <div className="text-sm text-gray-600">Total Users</div>
          </div>
          <div className="text-center p-6 bg-gray-50 rounded-lg">
            <div className="text-3xl font-bold text-green-600">{stats.verified_tailors}</div>
            <div className="text-sm text-gray-600">Verified Tailors</div>
          </div>
          <div className="text-center p-6 bg-gray-50 rounded-lg">
            <div className="text-3xl font-bold text-purple-600">${stats.revenue.toFixed(2)}</div>
            <div className="text-sm text-gray-600">Total Revenue</div>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Enhanced Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Logo size="lg" />
              <h1 className="ml-4 text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Welcome, {user?.name}</span>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Navigation Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'tailors', label: 'Tailors', icon: Crown },
              { id: 'customers', label: 'Customers', icon: Users },
              { id: 'all-users', label: 'All Users', icon: Users },
              { id: 'invitations', label: 'Invitations', icon: Mail },
              { id: 'analytics', label: 'Analytics', icon: TrendingUp }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'tailors' && renderTailors()}
        {activeTab === 'customers' && renderCustomers()}
        {activeTab === 'all-users' && renderAllUsers()}
        {activeTab === 'invitations' && renderInvitations()}
        {activeTab === 'analytics' && renderAnalytics()}
      </div>

      {/* Invitation Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Send Invitation</h3>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    value={invitationData.email}
                    onChange={(e) => setInvitationData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter email address"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={invitationData.role}
                    onChange={(e) => setInvitationData(prev => ({ ...prev, role: e.target.value as 'tailor' | 'customer' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="tailor">Tailor</option>
                    <option value="customer">Customer</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Personal Message (Optional)</label>
                  <textarea
                    value={invitationData.message}
                    onChange={(e) => setInvitationData(prev => ({ ...prev, message: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    placeholder="Add a personal message to the invitation..."
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowInviteModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendInvitation}
                    disabled={actionLoading === -1}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {actionLoading === -1 ? 'Sending...' : 'Send Invitation'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tier Upgrade Modal */}
      {showTierModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Upgrade User Tier</h3>
                <button
                  onClick={() => setShowTierModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <button
                    onClick={() => handleUpgradeTier(selectedUser?.id || '', 'free')}
                    className="p-4 border-2 border-gray-300 rounded-lg hover:border-gray-400 text-center"
                  >
                    <div className="text-lg font-semibold text-gray-700">Free</div>
                    <div className="text-sm text-gray-500">Basic features</div>
                  </button>
                  <button
                    onClick={() => handleUpgradeTier(selectedUser?.id || '', 'basic')}
                    className="p-4 border-2 border-blue-300 rounded-lg hover:border-blue-400 text-center"
                  >
                    <div className="text-lg font-semibold text-blue-700">Basic</div>
                    <div className="text-sm text-gray-500">Enhanced features</div>
                  </button>
                  <button
                    onClick={() => handleUpgradeTier(selectedUser?.id || '', 'premium')}
                    className="p-4 border-2 border-purple-300 rounded-lg hover:border-purple-400 text-center"
                  >
                    <div className="text-lg font-semibold text-purple-700">Premium</div>
                    <div className="text-sm text-gray-500">All features</div>
                  </button>
                </div>
                
                <div className="flex justify-end pt-4">
                  <button
                    onClick={() => setShowTierModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">User Details</h3>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedUser.full_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedUser.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Role</label>
                    <p className="mt-1 text-sm text-gray-900 capitalize">{selectedUser.role}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tier</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTierColor(selectedUser.tier || 'free')}`}>
                      {selectedUser.tier || 'free'}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      selectedUser.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {selectedUser.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Profile Completion</label>
                    <div className="flex items-center">
                      <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                        <div 
                          className={`h-2 rounded-full ${getCompletionColor(selectedUser.profile_completion_percentage || 0)}`}
                          style={{ width: `${selectedUser.profile_completion_percentage || 0}%` }}
                        ></div>
                      </div>
                      <span className={`text-xs font-medium ${getCompletionColor(selectedUser.profile_completion_percentage || 0)}`}>
                        {selectedUser.profile_completion_percentage || 0}%
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedUser.phone_number || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Created</label>
                    <p className="mt-1 text-sm text-gray-900">{new Date(selectedUser.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Delete User</h3>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setUserToDelete(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                    <h4 className="text-sm font-medium text-red-800">Warning: This action cannot be undone</h4>
                  </div>
                  <p className="mt-2 text-sm text-red-700">
                    Deleting this user will permanently remove all their data including:
                  </p>
                  <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                    <li>User account and profile</li>
                    <li>All orders and appointments</li>
                    <li>Customer/tailor profile data</li>
                    <li>Portfolio items and measurements</li>
                    <li>All related records</li>
                  </ul>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">User Information:</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Name:</span>
                      <span className="ml-2 text-gray-900">{userToDelete.full_name}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Email:</span>
                      <span className="ml-2 text-gray-900">{userToDelete.email}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Role:</span>
                      <span className="ml-2 text-gray-900 capitalize">{userToDelete.role}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Tier:</span>
                      <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTierColor(userToDelete.tier || 'free')}`}>
                        {userToDelete.tier || 'free'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setUserToDelete(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    disabled={actionLoading === parseInt(userToDelete.id)}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteUser}
                    disabled={actionLoading === parseInt(userToDelete.id)}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {actionLoading === parseInt(userToDelete.id) ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Delete Permanently
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Confirmation Modal */}
      {showPasswordResetModal && userToResetPassword && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Reset Password</h3>
                <button
                  onClick={() => {
                    setShowPasswordResetModal(false);
                    setUserToResetPassword(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <KeyRound className="w-5 h-5 text-orange-600 mr-2" />
                    <h4 className="text-sm font-medium text-orange-800">Password Reset Information</h4>
                  </div>
                  <p className="mt-2 text-sm text-orange-700">
                    This will reset the user's password to a default value. They will be required to change it on their next login.
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">User Details</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Name:</span>
                      <span className="ml-2 text-gray-900">{userToResetPassword.full_name}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Email:</span>
                      <span className="ml-2 text-gray-900">{userToResetPassword.email}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Role:</span>
                      <span className="ml-2 text-gray-900 capitalize">{userToResetPassword.role}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">New Password:</span>
                      <span className="ml-2 text-gray-900 font-mono">Pass123!</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => {
                      setShowPasswordResetModal(false);
                      setUserToResetPassword(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    disabled={actionLoading === parseInt(userToResetPassword.id)}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleResetPassword}
                    disabled={actionLoading === parseInt(userToResetPassword.id)}
                    className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {actionLoading === parseInt(userToResetPassword.id) ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Resetting...
                      </>
                    ) : (
                      <>
                        <KeyRound className="w-4 h-4" />
                        Reset Password
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;









